import type * as CSS from 'csstype'
import { system, Config } from '@styled-system/core'
import { ResponsiveValue } from '../utils/types'

export interface FlexContainerProps {
  /**
   * The CSS align-content property sets how the browser distributes space between and around content items
   * along the cross-axis of a flexbox container, and the main-axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content)
   */
  alignContent?: ResponsiveValue<CSS.AlignContentProperty>
  /**
   * The CSS align-items property sets the align-self value on all direct children as a group. The align-self
   * property sets the alignment of an item within its containing block.
   *
   * In Flexbox it controls the alignment of items on the Cross Axis, in Grid Layout it controls the alignment
   * of items on the Block Axis within their grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)
   */
  alignItems?: ResponsiveValue<CSS.AlignItemsProperty>
  /**
   * The cross-axis proxy for the CSS align-items property sets the align-self value on all direct children as a group. The align-self
   * property sets the alignment of an item within its containing block.
   *
   * In Flexbox it controls the alignment of items on the Cross Axis, in Grid Layout it controls the alignment
   * of items on the Block Axis within their grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)
   */
  alignCrossAxis?: ResponsiveValue<CSS.AlignItemsProperty>
  /**
   * The flex-direction CSS property specifies how flex items are placed in the flex container defining the main
   * axis and the direction (normal or reversed).
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   */
  flexDirection?: ResponsiveValue<CSS.FlexDirectionProperty>
  /**
   * The flex-wrap CSS property sets whether flex items are forced onto one line or can wrap onto multiple lines.
   * If wrapping is allowed, it sets the direction that lines are stacked.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap)
   */
  flexWrap?: ResponsiveValue<CSS.FlexWrapProperty>
  /**
   * The CSS justify-content property defines how the browser distributes space between and around content items
   * along the main-axis of a flex container, and the inline axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)
   */
  justifyContent?: ResponsiveValue<CSS.JustifyContentProperty>
  /**
   * The align-main-axis proxy for the CSS justify-content property defines how the browser distributes space between and around content items
   * along the main-axis of a flex container, and the inline axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)
   */
  alignMainAxis?: ResponsiveValue<CSS.JustifyContentProperty>
  /**
   * The CSS justify-items property defines the default justify-self for all items of the box, giving them all
   * a default way of justifying each box along the appropriate axis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-items)
   */
  justifyItems?: ResponsiveValue<CSS.JustifyItemsProperty>
  /**
   * The display CSS property defines the display type of an element, which consists of the two basic qualities
   * of how an element generates boxes — the outer display type defining how the box participates in flow layout,
   * and the inner display type defining how the children of the box are laid out.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/display)
   */
  display?: ResponsiveValue<CSS.DisplayProperty>
}

export const config: Config = {
  alignContent: true,
  alignItems: true,
  alignCrossAxis: {
    property: 'alignItems',
    transform: alignmentShim
  },
  flexDirection: true,
  flexWrap: true,
  alignMainAxis: {
    property: 'justifyContent',
    transform: alignmentShim
  },
  justifyContent: true,
  justifyItems: true,
  display: true
}

export const flexContainerProps = system(config)

function alignmentShim(direction: CSS.SelfPosition) {
  switch (direction) {
    case 'start':
      return 'flex-start'

    case 'end':
      return 'flex-end'

    default:
      return direction
  }
}
